// Keep in sync with dentallink .../catalog/api/dto and .../manufacturer/api/dto.

/** dentallink .../material UnitOfMeasure. Conversion only within a dimension. */
export type UnitOfMeasure = 'PCS' | 'MG' | 'G' | 'KG' | 'ML' | 'L';

export const UNIT_LABELS: Record<UnitOfMeasure, string> = {
  PCS: 'pcs',
  MG: 'mg',
  G: 'g',
  KG: 'kg',
  ML: 'ml',
  L: 'l'
};

export type CatalogStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export const CATALOG_STATUS_LABELS: Record<CatalogStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived'
};

export interface Catalog {
  id: number;
  code: string;
  name: string;
  description: string | null;
  sourceUrl: string | null;
  status: CatalogStatus;
  version: number;
  publishedAt: string | null;
  updatedAt: string | null;
  materialCount: number;
  packageCount: number;
  /** Always null on the admin side: there is no tenant to count for. */
  importedCount: number | null;
}

/** Body of POST and PUT /v1/admin/catalogs. The code is stored upper-case. */
export interface CatalogRequest {
  code: string;
  name: string;
  description?: string | null;
  sourceUrl?: string | null;
}

export interface CatalogPackage {
  id: number;
  manufacturerRef: string | null;
  barcode: string | null;
  name: string;
  packageQuantity: number;
  packageUnit: UnitOfMeasure;
  active: boolean;
}

export interface CatalogMaterial {
  id: number;
  materialKey: string;
  name: string;
  manufacturerId: number | null;
  manufacturerName: string | null;
  unit: UnitOfMeasure;
  wholeQuantitiesOnly: boolean;
  categoryName: string | null;
  family: string | null;
  shade: string | null;
  translucency: string | null;
  thicknessMm: number | null;
  diameterMm: number | null;
  variant: string | null;
  /** False once a later upload no longer lists it (RETIRE); retired rows are still returned. */
  active: boolean;
  packages: CatalogPackage[];
}

export interface CatalogMaterialsQuery {
  search?: string;
  category?: string;
  page?: number;
  size?: number;
}

/** What one CSV row (one package) would do to the catalog. */
export type CatalogRowAction = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'RETIRE' | 'ERROR';

export interface CatalogUploadRow {
  /** Null for a RETIRE row: it is something the file no longer has, so it has no line. */
  line: number | null;
  materialKey: string | null;
  materialName: string | null;
  manufacturerRef: string | null;
  packageName: string | null;
  action: CatalogRowAction;
  message: string | null;
}

export interface CatalogUploadTotals {
  create: number;
  update: number;
  unchanged: number;
  retire: number;
  error: number;
}

/**
 * What an upload would do, or — when `committed` — what it did. Preview and upload return the same
 * shape, so one view renders both.
 */
export interface CatalogUploadPlan {
  committed: boolean;
  version: number;
  /** Problems with the file as a whole; any of them means no row was read. */
  fileErrors: string[];
  rows: CatalogUploadRow[];
  totals: CatalogUploadTotals;
}

export interface Manufacturer {
  id: number;
  name: string;
  global: boolean;
}

/** GET list endpoints of these modules return backend common.dto.CollectionDTO: { data, size }. */
export interface Collection<T> {
  data: T[];
  size: number;
}
